import { useState, useEffect, useCallback, useMemo } from "react";
import { 
  Calendar, 
  CalendarDays, 
  Mail, 
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  Eye,
  Plus,
  Edit,
  Users,
  User,
  LayoutDashboard,
  Upload,
  Image as ImageIcon,
  Images,
  Ticket,
  Building,
  Settings,
  Key
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminBookings({ credentials, userRole }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  
  const canModify = userRole === 'admin';

  const headers = useMemo(() => {
    const auth = btoa(`${credentials.username}:${credentials.password}`);
    return { Authorization: `Basic ${auth}` };
  }, [credentials.username, credentials.password]);

  const fetchBookings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bookings`, { headers });
      setBookings(response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      console.error("Error fetching bookings:", error);
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/bookings/${id}`, { status }, { headers });
      toast.success(`Booking ${status}`);
      fetchBookings();
    } catch (error) {
      toast.error("Failed to update booking");
    }
  };

  const deleteBooking = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/bookings/${deleteId}`, { headers });
      toast.success("Booking deleted");
      setDeleteId(null);
      fetchBookings();
    } catch (error) {
      toast.error("Failed to delete booking");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "status-pending",
      confirmed: "status-available",
      cancelled: "status-booked"
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div data-testid="admin-bookings">
      <div className="flex justify-between items-center mb-8">
        <h1 className="heading-lg">Bookings</h1>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      ) : bookings.length === 0 ? (
        <Card className="card-heritage">
          <CardContent className="p-12 text-center">
            <Calendar className="w-12 h-12 text-[#D6D3D1] mx-auto mb-4" />
            <p className="text-[#78716C]">No bookings yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {bookings.map((booking) => (
            <Card key={booking.id} className={`card-heritage ${booking.is_internal ? 'border-l-4 border-l-[#D97706]' : ''}`} data-testid={`booking-${booking.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-[#1C1917]">
                        {booking.is_internal ? booking.purpose_of_hire?.replace("Internal: ", "") : booking.hirer_name}
                      </h3>
                      {getStatusBadge(booking.status)}
                      {booking.is_internal && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-[#FEF3C7] text-[#92400E]">
                          Internal
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-[#78716C]">
                      <p><Calendar className="w-4 h-4 inline mr-1" /> {booking.booking_date}</p>
                      <p><Clock className="w-4 h-4 inline mr-1" /> {booking.start_time} - {booking.end_time}</p>
                      <p><Users className="w-4 h-4 inline mr-1" /> {booking.is_internal ? (booking.hirer_name !== "Internal Staff" ? booking.hirer_name : "Staff") : booking.purpose_of_hire}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm" onClick={() => setSelectedBooking(booking)}>
                          <Eye size={16} className="mr-1" /> View
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg">
                        <DialogHeader>
                          <DialogTitle className="font-serif">
                            {booking.is_internal ? "Internal Booking Details" : "Booking Details"}
                          </DialogTitle>
                        </DialogHeader>
                        {selectedBooking && (
                          <div className="space-y-4 text-sm">
                            {selectedBooking.is_internal && (
                              <div className="bg-[#FEF3C7] border border-[#F59E0B] rounded-lg p-3 mb-4">
                                <p className="font-semibold text-[#92400E]">Internal Booking</p>
                              </div>
                            )}
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <span className="text-[#78716C]">{selectedBooking.is_internal ? "Event:" : "Name:"}</span>
                                <br />
                                {selectedBooking.is_internal ? selectedBooking.purpose_of_hire?.replace("Internal: ", "") : selectedBooking.hirer_name}
                              </div>
                              {!selectedBooking.is_internal && (
                                <div><span className="text-[#78716C]">Email:</span><br />{selectedBooking.email_address}</div>
                              )}
                              <div>
                                <span className="text-[#78716C]">{selectedBooking.is_internal ? "Organizer:" : "Phone:"}</span>
                                <br />
                                {selectedBooking.is_internal ? selectedBooking.hirer_name : selectedBooking.phone_number}
                              </div>
                              {!selectedBooking.is_internal && (
                                <div><span className="text-[#78716C]">Purpose:</span><br />{selectedBooking.purpose_of_hire}</div>
                              )}
                              <div><span className="text-[#78716C]">Date:</span><br />{selectedBooking.booking_date}</div>
                              <div><span className="text-[#78716C]">Time:</span><br />{selectedBooking.start_time} - {selectedBooking.end_time}</div>
                              <div><span className="text-[#78716C]">Attendees:</span><br />{selectedBooking.number_of_attendees || "N/A"}</div>
                              <div><span className="text-[#78716C]">Status:</span><br />{getStatusBadge(selectedBooking.status)}</div>
                            </div>
                            {selectedBooking.special_requests && (
                              <div>
                                <span className="text-[#78716C]">{selectedBooking.is_internal ? "Notes:" : "Special Requests:"}</span>
                                <p className="mt-1">{selectedBooking.special_requests}</p>
                              </div>
                            )}
                            {!selectedBooking.is_internal && selectedBooking.total_cost > 0 && (
                              <div className="bg-[#F5F5F0] p-3 rounded-lg">
                                <span className="text-[#78716C]">Total Cost:</span>
                                <p className="text-lg font-bold text-[#14532D]">£{selectedBooking.total_cost?.toFixed(2)}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>
                    {canModify && booking.status === "pending" && (
                      <>
                        <Button 
                          size="sm" 
                          className="bg-[#15803D] hover:bg-[#166534]"
                          onClick={() => updateStatus(booking.id, "confirmed")}
                        >
                          <CheckCircle size={16} className="mr-1" /> Confirm
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => updateStatus(booking.id, "cancelled")}
                        >
                          <XCircle size={16} className="mr-1" /> Cancel
                        </Button>
                      </>
                    )}
                    {canModify && (
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-[#B91C1C]"
                        onClick={() => setDeleteId(booking.id)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Booking?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the booking.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteBooking} className="bg-[#B91C1C] hover:bg-[#991B1B]">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Events Component
